import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from "@mui/material";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function localDate(offsetDays = 0) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Colombo", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date()).reduce((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
  const date = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) + offsetDays));
  return date.toISOString().slice(0, 10);
}

function displayDateTime(value) {
  if (!value) return "—";
  // The database stores factory local DATETIME. Do not parse it as browser UTC.
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}` : String(value);
}

function displayLastUpdated(epoch) {
  const seconds = Number(epoch);
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  return new Date(seconds * 1000).toLocaleString("en-LK", {
    timeZone: "Asia/Colombo", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
  });
}

function planStatus(plan, now) {
  const start = String(plan.planningFrom || "").replace("T", " ");
  const end = String(plan.planingTo || "").replace("T", " ");
  if (start <= now && now <= end) return "Within window";
  return start > now ? "Upcoming" : "Elapsed";
}

export default function MachineSupervisorPlansView({ supervisorId, allocatedMachines = [], t }) {
  const [dateMode, setDateMode] = useState("single");
  const [startDate, setStartDate] = useState(() => localDate());
  const [endDate, setEndDate] = useState(() => localDate(6));
  const [machineCode, setMachineCode] = useState("");
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [truncated, setTruncated] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const machines = useMemo(() => allocatedMachines.map((machine) => ({
    code: machine.MachineCode || machine.machineCode,
    name: machine.MachineDescription || machine.description || "",
  })).filter((machine) => machine.code).sort((a, b) => a.code.localeCompare(b.code)), [allocatedMachines]);

  const handleStartDateChange = (event) => {
    const nextStart = event.target.value;
    setStartDate(nextStart);
    if (dateMode === "single" || !nextStart || !endDate) return;
    const days = (Date.parse(endDate + "T00:00:00Z") - Date.parse(nextStart + "T00:00:00Z")) / 86400000;
    if (days < 0 || days >= 31) setEndDate(nextStart);
  };

  const effectiveEndDate = dateMode === "single" ? startDate : endDate;
  const validDates = startDate && effectiveEndDate && startDate <= effectiveEndDate &&
    (Date.parse(effectiveEndDate + "T00:00:00Z") - Date.parse(startDate + "T00:00:00Z")) < 31 * 86400000;

  const handleDateModeChange = (_, nextMode) => {
    if (!nextMode) return;
    if (nextMode === "range") {
      const days = (Date.parse(endDate + "T00:00:00Z") - Date.parse(startDate + "T00:00:00Z")) / 86400000;
      if (days < 0 || days >= 31) setEndDate(startDate);
    }
    setDateMode(nextMode);
  };

  const fetchPlans = useCallback(async (signal) => {
    if (!supervisorId || !validDates) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/machinesupervisor/plans/${encodeURIComponent(supervisorId)}`,
        { params: { startDate, endDate: effectiveEndDate, ...(machineCode ? { machineCode } : {}) }, signal },
      );
      setPlans(Array.isArray(response.data?.plans) ? response.data.plans : []);
      setTruncated(Boolean(response.data?.truncated));
      setRefreshedAt(new Date());
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return;
      setPlans([]);
      setTruncated(false);
      setError(err?.response?.data?.error || err?.response?.data?.message || "Could not load machine plans.");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [supervisorId, startDate, effectiveEndDate, machineCode, validDates]);

  useEffect(() => {
    const controller = new AbortController();
    fetchPlans(controller.signal);
    return () => controller.abort();
  }, [fetchPlans]);

  const groups = useMemo(() => {
    const byMachine = new Map();
    for (const plan of plans) {
      if (!byMachine.has(plan.machineCode)) byMachine.set(plan.machineCode, []);
      byMachine.get(plan.machineCode).push(plan);
    }
    return [...byMachine.entries()].map(([code, rows]) => {
      const byWindow = new Map();
      for (const plan of rows) {
        const key = `${plan.planningFrom}|${plan.planingTo}`;
        if (!byWindow.has(key)) byWindow.set(key, []);
        byWindow.get(key).push(plan);
      }
      return {
        code,
        description: rows[0]?.MachineDescription,
        jobCount: rows.length,
        windows: [...byWindow.entries()].map(([key, jobs]) => ({
          key,
          from: jobs[0].planningFrom,
          through: jobs[0].planingTo,
          lastUpdatedEpoch: Math.max(...jobs.map((job) => Number(job.lastUpdatedEpoch) || 0)),
          uploadedAtEpoch: Math.max(...jobs.map((job) => Number(job.uploadedAtEpoch) || 0)),
          uploadCount: new Set(jobs.map((job) => Number(job.uploadedAtEpoch) || 0).filter(Boolean)).size,
          jobs,
        })),
      };
    });
  }, [plans]);

  const now = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Colombo", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).format(new Date());

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: t.s1, border: `1px solid ${t.border}` }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: t.heading }}>Machine Plans</Typography>
            <Typography variant="body2" sx={{ color: t.muted }}>
              Plans for your allocated machines, grouped by the date each plan starts. Schedule labels reflect plan windows, not production progress. Times shown in Sri Lanka time.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label={`${plans.length} jobs`} size="small" />
            <Chip label={`${groups.length} machines`} size="small" variant="outlined" />
            <Button variant="outlined" onClick={() => fetchPlans()} disabled={loading || !validDates}>
              {loading ? <CircularProgress size={18} /> : "Refresh"}
            </Button>
          </Stack>
        </Stack>
        <ToggleButtonGroup value={dateMode} exclusive onChange={handleDateModeChange}
          size="small" aria-label="Plan date selection" sx={{ mb: 1.5 }}>
          <ToggleButton value="single">One plan day</ToggleButton>
          <ToggleButton value="range">Date range</ToggleButton>
        </ToggleButtonGroup>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField type="date" size="small" label={dateMode === "single" ? "Plan date" : "From"}
            value={startDate} onChange={handleStartDateChange} InputLabelProps={{ shrink: true }} />
          {dateMode === "range" && <TextField type="date" size="small" label="Through" value={endDate}
            onChange={(event) => setEndDate(event.target.value)} InputLabelProps={{ shrink: true }} />}
          <TextField select size="small" label="Machine" value={machineCode}
            onChange={(event) => setMachineCode(event.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="">All allocated machines</MenuItem>
            {machines.map((machine) => <MenuItem key={machine.code} value={machine.code}>
              {machine.code}{machine.name ? ` · ${machine.name}` : ""}
            </MenuItem>)}
          </TextField>
        </Stack>
        <Typography variant="caption" sx={{ color: t.muted, display: "block", mt: 1.25 }}>
          Each plan belongs to the date its planning window starts; a plan may end the following morning.
        </Typography>
        {!validDates && <Alert severity="warning" sx={{ mt: 2 }}>Select a valid date range of up to 31 days.</Alert>}
        {refreshedAt && <Typography variant="caption" sx={{ color: t.muted, display: "block", mt: 1.5 }}>
          View refreshed {refreshedAt.toLocaleTimeString("en-LK", { timeZone: "Asia/Colombo" })} (Sri Lanka)
        </Typography>}
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {truncated && <Alert severity="warning" sx={{ mb: 2 }}>
        More than 1,500 records match. Narrow the date range or select one machine to see all jobs.
      </Alert>}
      {loading && <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress /></Box>}
      {!loading && !error && !plans.length && validDates &&
        <Alert severity="info">No plans found for the selected machines and dates.</Alert>}
      {!loading && groups.map((group) => (
        <Paper key={group.code} sx={{ mb: 2, borderRadius: 2, bgcolor: t.s1, border: `1px solid ${t.border}` }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 800, color: t.heading }}>{group.code}</Typography>
            <Chip label={`${group.jobCount} jobs · ${group.windows.length} plan windows`} size="small" variant="outlined" />
            <Typography variant="body2" sx={{ color: t.muted }}>{group.description}</Typography>
          </Stack>
          {group.windows.map((window) => {
            const status = planStatus({ planningFrom: window.from, planingTo: window.through }, now);
            return <Box key={window.key} sx={{ borderTop: `1px solid ${t.border}` }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}
                flexWrap="wrap" sx={{ px: 2, py: 1.5, bgcolor: t.s2 }}>
                <Typography sx={{ fontWeight: 750, color: t.heading }}>
                  {displayDateTime(window.from)} → {displayDateTime(window.through)}
                </Typography>
                <Chip label={`${window.jobs.length} jobs`} size="small" variant="outlined" />
                <Chip label={status} size="small"
                  color={status === "Within window" ? "success" : "default"} variant="outlined" />
                <Typography variant="caption" sx={{ color: t.muted, ml: { sm: "auto" } }}>
                  {window.uploadCount > 1 ? "Latest upload in plan" : "Plan uploaded"}: {window.uploadedAtEpoch ? displayLastUpdated(window.uploadedAtEpoch) : "Unavailable for older plans"}<br />
                  Latest plan update: {displayLastUpdated(window.lastUpdatedEpoch)} (Sri Lanka)
                </Typography>
              </Stack>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 950 }}>
                  <TableHead><TableRow>
                    {["Seq.", "Job number", "Customer / description", "Operation",
                      "WO qty", "OP qty", "Planned qty", "Priority", "Plan time", "Due date"].map((heading) =>
                      <TableCell key={heading} sx={{ fontWeight: 700, color: t.heading }}>{heading}</TableCell>)}
                  </TableRow></TableHead>
                  <TableBody>{window.jobs.map((plan) => (
                    <TableRow key={plan.id} hover>
                      <TableCell>{plan.jobSequenceNumber ?? "—"}</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{plan.jobNumber || "—"}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="body2">{plan.customer || "—"}</Typography>
                        <Typography variant="caption" display="block" sx={{ color: t.muted, mt: 0.5 }}>
                          {plan.WOdescription || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>{plan.OpDesctiption || "—"}</TableCell>
                      {[plan.WOQty, plan.OPQty, plan.plannedQty].map((value, index) =>
                        <TableCell key={index} align="right">
                          {value == null ? "—" : Number(value).toLocaleString()}
                        </TableCell>)}
                      <TableCell>{plan.WOpriority || "—"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {plan.SetupAndOpeTime == null ? "—" : plan.SetupAndOpeTime} {plan.TimeUnit || ""}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{plan.WOduedate || "—"}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </Box>
            </Box>;
          })}
        </Paper>
      ))}
    </Box>
  );
}
